import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import { IDefaultReducerState } from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  prepEntityForAPICall,
} from 'utils/sliceHelpers';
import {
  ISOAPConnectionSettings,
  createSOAPConnectionSettings,
} from '@timeedit/types';
import api from '../services/api.service';

export type TAppsIntegrationSettings = IDefaultReducerState &
  ISOAPConnectionSettings;

export const initialState: TAppsIntegrationSettings = {
  loading: false,
  hasErrors: false,
  username: '',
  password: '',
  certificateKey: '',
  baseUrl: '',
};

// Slice
const slice = createSlice({
  name: 'appsIntegrationSettings',
  initialState,
  reducers: {
    fetchAppsIntegrationSettingsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchAppsIntegrationSettingsSuccess: handleIntegrationSettingsSuccess,
    fetchAppsIntegrationSettingsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    saveAppsIntegrationSettingsRequest: (state) => {
      isLoadingRequest(state);
    },
    saveAppsIntegrationSettingsSuccess: handleIntegrationSettingsSuccess,
    saveAppsIntegrationSettingsFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

function handleIntegrationSettingsSuccess(
  state: TAppsIntegrationSettings,
  { payload }: PayloadAction<ISOAPConnectionSettings>
) {
  const appsIntegrationSettings = createSOAPConnectionSettings(payload);
  state.username = appsIntegrationSettings.username;
  state.password = appsIntegrationSettings.password;
  state.certificateKey = appsIntegrationSettings.certificateKey;
  state.baseUrl = appsIntegrationSettings.baseUrl;
  finishedLoadingSuccess(state);
}

export default slice.reducer;

// Selectors
export const appsIntegrationSettingsLoading = (state: TRootState) =>
  state.appsIntegrationSettings.loading;

export const appsIntegrationSettingsSelector = createSelector(
  (state: TRootState) => state.appsIntegrationSettings,
  (appsIntegrationSettings) => {
    const { username, password, certificateKey, baseUrl } =
      appsIntegrationSettings;
    return { username, password, certificateKey, baseUrl };
  }
);

// Actions
export const {
  fetchAppsIntegrationSettingsRequest,
  fetchAppsIntegrationSettingsSuccess,
  fetchAppsIntegrationSettingsFailure,
  saveAppsIntegrationSettingsRequest,
  saveAppsIntegrationSettingsSuccess,
  saveAppsIntegrationSettingsFailure,
} = slice.actions;

export const fetchAppsIntegrationSettings = createAppAsyncThunk(
  'appsIntegrationSettings/fetchAppsIntegrationSettings',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchAppsIntegrationSettingsRequest());
      const result = await api.get({
        endpoint: `/apps/integration-settings/`,
      });
      dispatch(fetchAppsIntegrationSettingsSuccess(result));
    } catch (e) {
      dispatch(fetchAppsIntegrationSettingsFailure());
      console.error(e);
    }
  }
);

export const saveAppsIntegrationSettings = createAppAsyncThunk<
  void,
  ISOAPConnectionSettings
>(
  'appsIntegrationSettings/saveAppsIntegrationSettings',
  async (appsIntegrationSettings, { dispatch }) => {
    try {
      dispatch(saveAppsIntegrationSettingsRequest());
      const preppedResource = prepEntityForAPICall(
        appsIntegrationSettings,
        true
      );
      const result = await api.patch({
        endpoint: `/apps/integration-settings`,
        data: preppedResource,
      });
      dispatch(saveAppsIntegrationSettingsSuccess(result));
    } catch (error) {
      dispatch(saveAppsIntegrationSettingsFailure());
      console.error(error);
    }
  }
);
